'use client';

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface ExpenseItem {
  id: string;
  category: string;
  amount: number;
}

interface ExpenseCategoryChartProps {
  expenses: ExpenseItem[];
}

const CATEGORY_COLORS_CHART = [
  'var(--primary)',
  'var(--info)',
  'var(--accent)',
  'var(--warning)',
  '#a855f7',
  '#ec4899',
  'var(--muted-foreground)',
];

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-xl p-3 shadow-xl">
        <p className="text-xs font-semibold text-foreground mb-1">{label}</p>
        <p className="text-sm font-bold amount-display text-foreground">₹{payload[0].value.toLocaleString('en-IN')}</p>
      </div>
    );
  }
  return null;
};

export default function ExpenseCategoryChart({ expenses }: ExpenseCategoryChartProps) {
  // Aggregate by category
  const categoryMap: Record<string, number> = {};
  expenses.forEach((e) => {
    categoryMap[e.category] = (categoryMap[e.category] || 0) + e.amount;
  });

  const data = Object.entries(categoryMap)
    .map(([category, total]) => ({ category: category.replace(' (Loading)', '').replace(' (Unloading)', ' (UL)').replace('Transport / Lorry', 'Transport').replace('Market Fee (Tolai)', 'Market Fee').replace('Weighbridge Charges', 'Weighbridge'), total }))
    .sort((a, b) => b.total - a.total);

  return (
    <div className="card-base p-4 h-full">
      <h3 className="text-sm font-semibold text-foreground mb-1">Category-wise Expense Breakdown</h3>
      <p className="text-xs text-muted-foreground mb-4">All active lots combined</p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="category"
            tick={{ fontSize: 9, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            angle={-30}
            textAnchor="end"
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="total" radius={[6, 6, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`exp-bar-${entry.category}`} fill={CATEGORY_COLORS_CHART[index % CATEGORY_COLORS_CHART.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}